import { API, CurrencyType, TFile } from '@brg/api';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { ImmediatePurchaseProps, OptionProps, OrderInitial, Product, ProductRequest, initOrderRequest } from '../constants/types';
import economicUrlApi from "../helpers/economicUrlApi";
import {DeliveryRequest} from "../constants/DeliveryRequest";
import {EntityPaymentTypes} from "../constants/payment";


export const initOrder = createAsyncThunk<any, initOrderRequest>(
    'order/initOrder',
    async (req: initOrderRequest, thuncApi) => {
        let order: any
        try {
            if (req.orderId) {
                order = await economicUrlApi().getOrder(req.orderId)
            } else {
                order = await economicUrlApi().createOrder(req.productId, req.currencyType, req.paymentEntityType, req.options)
            }
            if((order?.ErrorStatusCode === 500) && order?.ErrorMessage) {
                return thuncApi.rejectWithValue(order.ErrorMessage)
            }
            const availableCurrencies = req.preventGetCurrencies ? null : await economicUrlApi().getAvailableCurrencies([{
                id: req.productId,
                type: req.paymentEntityType
            }])
            return {
                order,
                productInfo: req.productInfo,
                availableCurrencies,
            }
        } catch (err: any) {
            return thuncApi.rejectWithValue(err?.message)
        }
    }
)

export const purchaseOrder = createAsyncThunk<any, any>(
    'order/{orderId}/purchase',
    async (req: any) => {
        const purchase = await economicUrlApi().purchaseOrder(req.orderId)
        return {
            purchase
        }
    }
)

/*
 * запрос на создание ордера с одновременной оплатой (без добавления/удаления других товаров и пр.)
 */
export const immediatePurchaseOrder = createAsyncThunk<any, ImmediatePurchaseProps>(
    'order/immediatePurchaseOrder',
    async (req: ImmediatePurchaseProps) => {

        const order: any = await economicUrlApi().createAndPurchaseOrder(req.productId, req.currencyType, req);

       if (order?.products?.length) {
            order.products.push({...req.productInfo, ...order.products.pop()});
       }
        return {
            order,
            productInfo: req.productInfo,
        }
    }
)

export const checkAvailableCurrencies = createAsyncThunk<any, Omit<ProductRequest, 'productInfo'>>(
    'order/checkAvailableCurrencies',
    async (req: Omit<ProductRequest, 'productInfo'>) => {
        const availableCurrencies = await economicUrlApi().getAvailableCurrencies([{
            id: req.productId,
            type: req.paymentEntityType
        }])
        return {
            availableCurrencies,
        }
    }
)

interface initOrderWithManyProductsRequest {
    products: ProductRequest[]
    paymentEntityType: EntityPaymentTypes,
    currencyType: CurrencyType
}

export const initOrderWithManyProducts = createAsyncThunk<any, initOrderWithManyProductsRequest>(
    'order/initOrderWithManyProducts',
    async (req: initOrderWithManyProductsRequest) => {
        const products = req.products.map(product => {
            return {
                id: product.productId,
                type: product.paymentEntityType
            }
        })
        let order: any = await economicUrlApi().createOrderWithManyProducts(products, req.currencyType)
        const availableCurrencies = await economicUrlApi().getAvailableCurrencies(products)
        return {
            order,
            products: req.products,
            availableCurrencies
        }
    }
)

interface GetProductsPrices {
    id: string
    type: EntityPaymentTypes,
}

/*
* запрос на получение списка рассчитанных цен для допустимых для данного продукта валют. Используется (пока) для варианта ордера с немедленной оплаты
*/
export const getProductsPrices = createAsyncThunk<any, GetProductsPrices>(
    'order/getProductsPrices',
    async (req: GetProductsPrices) => {
        let availableCurrencies

       if (req.type === EntityPaymentTypes.book) {
            availableCurrencies = await economicUrlApi().getBookPrices(req?.id)
        } else if ([EntityPaymentTypes.product_card, EntityPaymentTypes.participation_card, EntityPaymentTypes.course].includes(req.type)) {
            availableCurrencies = await economicUrlApi().getProductCardPricesV2({id: req?.id})
        } else {
            availableCurrencies = await economicUrlApi().getPrices(req?.id, req.type)
        }

        return availableCurrencies
    }
)

interface addProductRequest {
    orderId: string
    productId: string,
    paymentEntityType: EntityPaymentTypes,
    productInfo: {
        preview: TFile[] | "calendar"
        name: string,
        summary: {
            [key: string]: string
        }
    },
}


export const addProduct = createAsyncThunk<any, addProductRequest>(
    'order/addProduct',
    async (req: addProductRequest) => {
        try {
            await economicUrlApi().addProduct(req.orderId, req.productId, req.paymentEntityType)
        } catch (e) {
            console.log(e)
        } finally {
            const order = await economicUrlApi().getOrder(req.orderId)
            return {
                id: req.productId,
                type: req.paymentEntityType,
                name: req.productInfo.name,
                summary: req.productInfo.summary,
                preview: req.productInfo.preview
            } as Product
        }

    }
)

interface addDelveryInfoRequest {
    orderId: string,
    info: DeliveryRequest
}

export const addDeliveryInfo = createAsyncThunk<any, addDelveryInfoRequest>(
    'order/addDeliveryInfo',
    async (req: addDelveryInfoRequest) => {
        // console.log('REQ', req)
        try {
            console.log('REQ', req)
            await economicUrlApi().addDeliveryInfo(req.info, req.orderId)
        } catch (e) {
            console.log(e)
        } finally {
            const order = await economicUrlApi().getOrder(req.orderId)
            return {
                info: req.info,
                fullData: order
            }
        }
    }
)

export const getAvailableTariffs = createAsyncThunk<any, any>(
    'order/getAvailableTariffes',
    async (req: any) => {
        const normalizedReq = req?.entityType === EntityPaymentTypes.course
            ? {
                ...req,
                entityType: EntityPaymentTypes.product_card,
                cardId: req?.cardId || req?.productId || req?.id || "",
                productId: undefined
            }
            : req;

        const availableTariffes = normalizedReq?.entityType === EntityPaymentTypes.book
            ? await economicUrlApi().getBookTariffes({bookCardId: normalizedReq?.bookCardId})
            : await economicUrlApi().getAvailableTariffes(normalizedReq)
        return {
            data: availableTariffes?.data,
            entityType: normalizedReq?.entityType
        }
    }
)

export const calculateCustomPrise = createAsyncThunk<any, any>(
    'order/calculateCustomPrise',
    async (req: any, thuncApi) => {
        let calculatedPrise
        try {
            calculatedPrise = await economicUrlApi().calculateCustomPrise(req)
            console.log('changePrice calculateCustomPrise', calculatedPrise);
            return calculatedPrise
        } catch (error: any) {
            thuncApi.rejectWithValue(error?.message)
        }
    }
)

export const getDeliveryTypes = createAsyncThunk<any, any>(
    'order/getDeliveryTypes',
    async (req: any, thuncApi) => {
        try {
            const deliveryTypesRequest: any = await economicUrlApi().getDeliveryTypes()
            const deliveryTypes = deliveryTypesRequest?.data
            if (deliveryTypes?.length) {
                thuncApi.dispatch(setProductFieldByKey({ key: 'deliveryMethod', value: deliveryTypes[0].key }))
            }
            return deliveryTypesRequest
        } catch (error: any) {
            thuncApi.rejectWithValue(error?.message)
        }
    }
)

const initialState: OrderInitial = {
    bookCard: null,
    bookCardT: Date.now(),
    activeTariff: null,
    paymentCurrencies: [{
        amount: null,
        currencyType: CurrencyType.RealMoney,
    }],
    availableTariffs: [], 
    currenciesloading: false,
    currencyType: CurrencyType.RealMoney,
    delivery: {
        countryRegionCity: "",
        streetHouseApartmentZipcode: "",
        fullName: "",
        email: "",
        phoneNumber: ""
    },
    deliveryTypes: null,
    error: null,
    flowSize: 1,
    flowStep: 1,
    flowType: null,
    immediatePurchaseProps: null,
    isModerBuying: true,
    loading: false,
    orderId: null,
    price: 0,
    products: [],
    productFields: null,
    tariffsLoading: false,
    totalPrices: null,
}

const OrderSlice = createSlice({
    name: "order",
    initialState,
    reducers: {
        clearOrderFields: () => initialState,
        clearOrderFieldsWithoutStep: (state) => {
            const {flowStep} = state
            return {...state, flowStep}
        },
        clearFlowFields(state, action) {
            state.flowSize = 1;
            state.flowStep = 1;
            state.flowType = action.payload;
        },
        updateProductSummary(state, action) {
            const productIndex = state.products.findIndex((prod) => prod.id === action.payload.productId)
            if (productIndex === -1) return
            state.products[productIndex].summary = { ...state.products[productIndex].summary, ...action.payload.summary }
        },
        clearImmediatePurchaseProps(state, action) {
            state.immediatePurchaseProps = null
        },
        setImmediatePurchaseProps(state, action) {
            const usedTariff = state.immediatePurchaseProps?.productId === action?.payload?.productId 
                ? state.activeTariff
                : undefined
            state.immediatePurchaseProps = action?.payload

            if (action.payload?.currencyType !== undefined) {
                state.currencyType = action.payload.currencyType
            }
            if (action.payload?.productInfo) {
                state.products = [{ ...action.payload?.productInfo }]
            }
            if (usedTariff || action.payload?.price !== undefined) {
                state.price = usedTariff?.creatingPrice || action.payload?.price
            }
        },
        goToNextStep(state) {
            state.flowStep = state.flowStep + 1
        },
        setBookCard(state, action) {
            state.bookCard = action.payload.bookCard
        },
        setBookCardT(state, action) {
            state.bookCardT = action.payload.bookCardT
        },
        clearBookCard(state) {
            state.bookCard = null
        },
        goToPrevStep(state) {
            state.flowStep = state.flowStep - 1
        },
        goToStep(state, action) {
            state.flowStep = action.payload
        },
        setActiveTariff(state, action) {
            state.activeTariff = action.payload
        },
        setIsModerBuying(state, action) {
            state.isModerBuying = action.payload
        },
        setAvailableTariffs(state, action) {
            state.availableTariffs = action.payload
            if (action.payload?.length) {
                state.activeTariff = action.payload[0]
            }
        },
        setProductFieldByKey(state, action) {
            if(!state.productFields) {
                state.productFields = {}
            }
            const fieldKey: 'productPrice' | 'productAmount' | 'deliveryMethod'= action.payload.key
            state.productFields[fieldKey] = action.payload.value
        },
        setProductFields(state, action) {
            state.productFields = action.payload
        },
        clearProductField(state, action) {
            state.productFields = null
        },
        setFlowSize(state, action) {
            state.flowSize = action.payload
        },
        setFlowStep(state, action) {
            state.flowStep = action.payload
        },
        setOrder(state, action) {
            return action.payload
        },
        setFlowType(state, action) {
            state.flowType = action.payload
        },
        setPaymentCurrencies(state, action) {
            state.paymentCurrencies = action.payload
        },        
        changeCurrencyType(state, action) {
            state.currencyType = action.payload
            if (state.immediatePurchaseProps) {
                state.immediatePurchaseProps.currencyType = action.payload
            }
        },
        setTotalPrices(state, action) {
            state.totalPrices = action.payload
        },
    },
    extraReducers: {
        [addDeliveryInfo.fulfilled.toString()]: (state, action) => {
            state.loading = false;
            state.delivery.countryRegionCity = action.payload.info.countryRegionCity
            state.delivery.streetHouseApartmentZipcode = action.payload.info.streetHouseApartmentZipcode
            state.delivery.fullName = action.payload.info.fullName
            state.delivery.email = action.payload.info.email
            state.delivery.phoneNumber = action.payload.info.phoneNumber
            state.fullData = action.payload.fullData
        },
        [addDeliveryInfo.pending.toString()]: (state) => {
            state.loading = true;
        },
        [addDeliveryInfo.rejected.toString()]: (state, action) => {
            state.loading = false;
            state.error = action.payload
        },
        [addProduct.fulfilled.toString()]: (state, action) => {
            state.loading = false;
            state.products.push(action.payload)
        },
        [addProduct.pending.toString()]: (state) => {
            state.loading = true;
        },
        [addProduct.rejected.toString()]: (state, action) => {
            state.loading = false;
            state.error = action.payload
        },
        [initOrder.fulfilled.toString()]: (state, action) => {
            state.loading = false;

            state.fullData = action.payload.order
            state.orderId = action.payload.order.id
            //product info received from API
            state.products = action.payload.order.products
            state.price = action.payload.order.totalAmount
            state.currencyType = action.payload.order.currencyType

            if(state.products.length) {
                //product info received from frontend
                state.products[0].preview = action.payload?.productInfo?.preview
                state.products[0].name = action.payload?.productInfo?.name
                state.products[0].summary = action.payload?.productInfo?.summary
            }

            state.availableCurrencies = action.payload?.availableCurrencies
        },
        [initOrder.pending.toString()]: (state) => {
            state.loading = true;
        },
        [initOrder.rejected.toString()]: (state, action) => {
            state.loading = false;
            state.error = action.payload
        }
        ,
        [immediatePurchaseOrder.fulfilled.toString()]: (state, action) => {
            state.loading = false;

            if (action?.payload?.order?.ErrorStatusCode === 500) return;

            state.fullData = action.payload.order
            state.orderId = action.payload.order.id
            //product info received from API
            state.products = action.payload.order.products
            state.price = action.payload.order.totalAmount
            state.currencyType = action.payload.order.currencyType

            if(state.products?.length) {
                //product info received from frontend
                state.products[0].preview = action.payload.productInfo.preview
                state.products[0].name = action.payload.productInfo.name
                state.products[0].summary = action.payload.productInfo.summary
            }
        }
        ,
        [immediatePurchaseOrder.pending.toString()]: (state) => {
            state.loading = true;
        }
        ,
        [immediatePurchaseOrder.rejected.toString()]: (state, action) => {
            state.loading = false;
            state.error = action.payload
        },
        [checkAvailableCurrencies.fulfilled.toString()]: (state, action) => {
            state.availableCurrencies = action.payload.availableCurrencies
        }
        ,
        [checkAvailableCurrencies.pending.toString()]: (state) => {
            state.availableCurrencies = [];
        },
        [initOrderWithManyProducts.fulfilled.toString()]: (state, action) => {
            state.loading = false;

            state.fullData = action.payload.order
            state.orderId = action.payload.order.id
            //product info received from API
            state.products = action.payload.order.products
            state.price = action.payload.order.totalAmount
            state.currencyType = action.payload.order.currencyType

            //product info received from frontend
            action.payload.products.forEach((product: ProductRequest, index: number) => {
                state.products[index].preview = product.productInfo.preview
                state.products[index].name = product.productInfo.name
                state.products[index].summary = product.productInfo.summary
            })

            state.availableCurrencies = action.payload.availableCurrencies
        },
        [initOrderWithManyProducts.pending.toString()]: (state) => {
            state.loading = true;
        },
        [initOrderWithManyProducts.rejected.toString()]: (state, action) => {
            state.loading = false;
            state.error = action.payload
        },
        [getProductsPrices.pending.toString()]: (state) => {
            state.currenciesloading = true;
        },
        [getProductsPrices.fulfilled.toString()]: (state, action) => {
            let newCurrencies = action.payload.prices
            state.currenciesloading = false;
            state.availableCurrencies = newCurrencies
        },
        [getProductsPrices.rejected.toString()]: (state, action) => {
            state.currenciesloading = false;
            state.error = action.payload
        },
        [getAvailableTariffs.pending.toString()]: (state) => {
            state.tariffsLoading = true;
        },
        [getAvailableTariffs.fulfilled.toString()]: (state, action) => {
            state.tariffsLoading = false;
            state.availableTariffs = action.payload?.data
            if (action.payload?.data?.length && (action.payload?.entityType === EntityPaymentTypes.book || action.payload?.entityType === EntityPaymentTypes.card)) {
                const prevIndex = action.payload?.data?.findIndex((i: any) => i?.type === state.activeTariff?.type)
                state.activeTariff = action.payload?.data[(prevIndex !== -1 && prevIndex !== undefined) ? prevIndex : action.payload?.data?.length >= 2 ? 1 :0]
                return
            }
            if (action.payload?.data?.length) {
                state.activeTariff = action.payload?.data[0]
            }
        },
        [getAvailableTariffs.rejected.toString()]: (state, action) => {
            state.tariffsLoading = false;
            state.error = action.payload
        },
        [calculateCustomPrise.rejected.toString()]: (state, action) => {
            state.error = action.payload
        },
        [getDeliveryTypes.pending.toString()]: (state) => {
            state.deliveryTypes = null;
        },
        [getDeliveryTypes.fulfilled.toString()]: (state, action) => {
            if(action.payload?.data?.length) {
                state.deliveryTypes = action.payload?.data.map((item: any) => {
                    return { key: item.key, label: item.value }
                })
            }
        },
        [getDeliveryTypes.rejected.toString()]: (state, action) => {
            state.error = action.payload
        },
    },
});

export default OrderSlice;

export const selectorOrder = (state: any): OrderInitial => state.order
export const selectorDeliveryType = (language: string) => (state: any): OptionProps[] => API.toLang(state.order.deliveryTypes, language)

export const {
    clearFlowFields,
    clearOrderFields,
    clearOrderFieldsWithoutStep,
    clearImmediatePurchaseProps,
    clearProductField,
    changeCurrencyType,
    goToNextStep,
    goToPrevStep,
    goToStep,
    setActiveTariff,
    setPaymentCurrencies,
    setAvailableTariffs,
    setImmediatePurchaseProps,
    setFlowSize,
    setIsModerBuying,
    setFlowType,
    setFlowStep,
    setOrder,
    setProductFields,
    setProductFieldByKey,
    updateProductSummary,
    setBookCard,
    setBookCardT,
    clearBookCard,
    setTotalPrices
} = OrderSlice.actions
import { API, CurrencyType, TFile } from '@brg/api';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { ImmediatePurchaseProps, OptionProps, OrderInitial, Product, ProductRequest, initOrderRequest } from '../constants/types';
import economicUrlApi from "../helpers/economicUrlApi";
import {DeliveryRequest} from "../constants/DeliveryRequest";
import {EntityPaymentTypes} from "../constants/payment";


export const initOrder = createAsyncThunk<any, initOrderRequest>(
    'order/initOrder',
    async (req: initOrderRequest, thuncApi) => {
        let order: any
        try {
            if (req.orderId) {
                order = await economicUrlApi().getOrder(req.orderId)
            } else {
                order = await economicUrlApi().createOrder(req.productId, req.currencyType, req.paymentEntityType, req.options)
            }
            if((order?.ErrorStatusCode === 500) && order?.ErrorMessage) {
                return thuncApi.rejectWithValue(order.ErrorMessage)
            }
            const availableCurrencies = req.preventGetCurrencies ? null : await economicUrlApi().getAvailableCurrencies([{
                id: req.productId,
                type: req.paymentEntityType
            }])
            return {
                order,
                productInfo: req.productInfo,
                availableCurrencies,
            }
        } catch (err: any) {
            return thuncApi.rejectWithValue(err?.message)
        }
    }
)

export const purchaseOrder = createAsyncThunk<any, any>(
    'order/{orderId}/purchase',
    async (req: any) => {
        const purchase = await economicUrlApi().purchaseOrder(req.orderId)
        return {
            purchase
        }
    }
)

/*
 * запрос на создание ордера с одновременной оплатой (без добавления/удаления других товаров и пр.)
 */
export const immediatePurchaseOrder = createAsyncThunk<any, ImmediatePurchaseProps>(
    'order/immediatePurchaseOrder',
    async (req: ImmediatePurchaseProps) => {

        const order: any = await economicUrlApi().createAndPurchaseOrder(req.productId, req.currencyType, req);

       if (order?.products?.length) {
            order.products.push({...req.productInfo, ...order.products.pop()});
       }
        return {
            order,
            productInfo: req.productInfo,
        }
    }
)

export const checkAvailableCurrencies = createAsyncThunk<any, Omit<ProductRequest, 'productInfo'>>(
    'order/checkAvailableCurrencies',
    async (req: Omit<ProductRequest, 'productInfo'>) => {
        const availableCurrencies = await economicUrlApi().getAvailableCurrencies([{
            id: req.productId,
            type: req.paymentEntityType
        }])
        return {
            availableCurrencies,
        }
    }
)

interface initOrderWithManyProductsRequest {
    products: ProductRequest[]
    paymentEntityType: EntityPaymentTypes,
    currencyType: CurrencyType
}

export const initOrderWithManyProducts = createAsyncThunk<any, initOrderWithManyProductsRequest>(
    'order/initOrderWithManyProducts',
    async (req: initOrderWithManyProductsRequest) => {
        const products = req.products.map(product => {
            return {
                id: product.productId,
                type: product.paymentEntityType
            }
        })
        let order: any = await economicUrlApi().createOrderWithManyProducts(products, req.currencyType)
        const availableCurrencies = await economicUrlApi().getAvailableCurrencies(products)
        return {
            order,
            products: req.products,
            availableCurrencies
        }
    }
)

interface GetProductsPrices {
    id: string
    type: EntityPaymentTypes,
}

/*
* запрос на получение списка рассчитанных цен для допустимых для данного продукта валют. Используется (пока) для варианта ордера с немедленной оплаты
*/
export const getProductsPrices = createAsyncThunk<any, GetProductsPrices>(
    'order/getProductsPrices',
    async (req: GetProductsPrices) => {
        let availableCurrencies

       if (req.type === EntityPaymentTypes.book) {
            availableCurrencies = await economicUrlApi().getBookPrices(req?.id)
        } else if ([EntityPaymentTypes.product_card, EntityPaymentTypes.participation_card, EntityPaymentTypes.course].includes(req.type)) {
            availableCurrencies = await economicUrlApi().getProductCardPricesV2({id: req?.id})
        } else {
            availableCurrencies = await economicUrlApi().getPrices(req?.id, req.type)
        }

        return availableCurrencies
    }
)

interface addProductRequest {
    orderId: string
    productId: string,
    paymentEntityType: EntityPaymentTypes,
    productInfo: {
        preview: TFile[] | "calendar"
        name: string,
        summary: {
            [key: string]: string
        }
    },
}


export const addProduct = createAsyncThunk<any, addProductRequest>(
    'order/addProduct',
    async (req: addProductRequest) => {
        try {
            await economicUrlApi().addProduct(req.orderId, req.productId, req.paymentEntityType)
        } catch (e) {
            console.log(e)
        } finally {
            const order = await economicUrlApi().getOrder(req.orderId)
            return {
                id: req.productId,
                type: req.paymentEntityType,
                name: req.productInfo.name,
                summary: req.productInfo.summary,
                preview: req.productInfo.preview
            } as Product
        }

    }
)

interface addDelveryInfoRequest {
    orderId: string,
    info: DeliveryRequest
}

export const addDeliveryInfo = createAsyncThunk<any, addDelveryInfoRequest>(
    'order/addDeliveryInfo',
    async (req: addDelveryInfoRequest) => {
        // console.log('REQ', req)
        try {
            console.log('REQ', req)
            await economicUrlApi().addDeliveryInfo(req.info, req.orderId)
        } catch (e) {
            console.log(e)
        } finally {
            const order = await economicUrlApi().getOrder(req.orderId)
            return {
                info: req.info,
                fullData: order
            }
        }
    }
)

export const getAvailableTariffs = createAsyncThunk<any, any>(
    'order/getAvailableTariffes',
    async (req: any) => {
        const normalizedReq = req?.entityType === EntityPaymentTypes.course
            ? {
                ...req,
                entityType: EntityPaymentTypes.product_card,
                cardId: req?.cardId || req?.productId || req?.id || "",
                productId: undefined
            }
            : req;

        const availableTariffes = normalizedReq?.entityType === EntityPaymentTypes.book
            ? await economicUrlApi().getBookTariffes({bookCardId: normalizedReq?.bookCardId})
            : await economicUrlApi().getAvailableTariffes(normalizedReq)
        return {
            data: availableTariffes?.data,
            entityType: normalizedReq?.entityType
        }
    }
)

export const calculateCustomPrise = createAsyncThunk<any, any>(
    'order/calculateCustomPrise',
    async (req: any, thuncApi) => {
        let calculatedPrise
        try {
            calculatedPrise = await economicUrlApi().calculateCustomPrise(req)
            console.log('changePrice calculateCustomPrise', calculatedPrise);
            return calculatedPrise
        } catch (error: any) {
            thuncApi.rejectWithValue(error?.message)
        }
    }
)

export const getDeliveryTypes = createAsyncThunk<any, any>(
    'order/getDeliveryTypes',
    async (req: any, thuncApi) => {
        try {
            const deliveryTypesRequest: any = await economicUrlApi().getDeliveryTypes()
            const deliveryTypes = deliveryTypesRequest?.data
            if (deliveryTypes?.length) {
                thuncApi.dispatch(setProductFieldByKey({ key: 'deliveryMethod', value: deliveryTypes[0].key }))
            }
            return deliveryTypesRequest
        } catch (error: any) {
            thuncApi.rejectWithValue(error?.message)
        }
    }
)

const initialState: OrderInitial = {
    bookCard: null,
    bookCardT: Date.now(),
    activeTariff: null,
    paymentCurrencies: [{
        amount: null,
        currencyType: CurrencyType.RealMoney,
    }],
    availableTariffs: [], 
    currenciesloading: false,
    currencyType: CurrencyType.RealMoney,
    delivery: {
        countryRegionCity: "",
        streetHouseApartmentZipcode: "",
        fullName: "",
        email: "",
        phoneNumber: ""
    },
    deliveryTypes: null,
    error: null,
    flowSize: 1,
    flowStep: 1,
    flowType: null,
    immediatePurchaseProps: null,
    isModerBuying: true,
    loading: false,
    orderId: null,
    price: 0,
    products: [],
    productFields: null,
    tariffsLoading: false,
    totalPrices: null,
}

const OrderSlice = createSlice({
    name: "order",
    initialState,
    reducers: {
        clearOrderFields: () => initialState,
        clearOrderFieldsWithoutStep: (state) => {
            const {flowStep} = state
            return {...state, flowStep}
        },
        clearFlowFields(state, action) {
            state.flowSize = 1;
            state.flowStep = 1;
            state.flowType = action.payload;
        },
        updateProductSummary(state, action) {
            const productIndex = state.products.findIndex((prod) => prod.id === action.payload.productId)
            if (productIndex === -1) return
            state.products[productIndex].summary = { ...state.products[productIndex].summary, ...action.payload.summary }
        },
        clearImmediatePurchaseProps(state, action) {
            state.immediatePurchaseProps = null
        },
        setImmediatePurchaseProps(state, action) {
            const usedTariff = state.immediatePurchaseProps?.productId === action?.payload?.productId 
                ? state.activeTariff
                : undefined
            state.immediatePurchaseProps = action?.payload

            if (action.payload?.currencyType !== undefined) {
                state.currencyType = action.payload.currencyType
            }
            if (action.payload?.productInfo) {
                state.products = [{ ...action.payload?.productInfo }]
            }
            if (usedTariff || action.payload?.price !== undefined) {
                state.price = usedTariff?.creatingPrice || action.payload?.price
            }
        },
        goToNextStep(state) {
            state.flowStep = state.flowStep + 1
        },
        setBookCard(state, action) {
            state.bookCard = action.payload.bookCard
        },
        setBookCardT(state, action) {
            state.bookCardT = action.payload.bookCardT
        },
        clearBookCard(state) {
            state.bookCard = null
        },
        goToPrevStep(state) {
            state.flowStep = state.flowStep - 1
        },
        goToStep(state, action) {
            state.flowStep = action.payload
        },
        setActiveTariff(state, action) {
            state.activeTariff = action.payload
        },
        setIsModerBuying(state, action) {
            state.isModerBuying = action.payload
        },
        setAvailableTariffs(state, action) {
            state.availableTariffs = action.payload
            if (action.payload?.length) {
                state.activeTariff = action.payload[0]
            }
        },
        setProductFieldByKey(state, action) {
            if(!state.productFields) {
                state.productFields = {}
            }
            const fieldKey: 'productPrice' | 'productAmount' | 'deliveryMethod'= action.payload.key
            state.productFields[fieldKey] = action.payload.value
        },
        setProductFields(state, action) {
            state.productFields = action.payload
        },
        clearProductField(state, action) {
            state.productFields = null
        },
        setFlowSize(state, action) {
            state.flowSize = action.payload
        },
        setFlowStep(state, action) {
            state.flowStep = action.payload
        },
        setOrder(state, action) {
            return action.payload
        },
        setFlowType(state, action) {
            state.flowType = action.payload
        },
        setPaymentCurrencies(state, action) {
            state.paymentCurrencies = action.payload
        },        
        changeCurrencyType(state, action) {
            state.currencyType = action.payload
            if (state.immediatePurchaseProps) {
                state.immediatePurchaseProps.currencyType = action.payload
            }
        },
        setTotalPrices(state, action) {
            state.totalPrices = action.payload
        },
    },
    extraReducers: {
        [addDeliveryInfo.fulfilled.toString()]: (state, action) => {
            state.loading = false;
            state.delivery.countryRegionCity = action.payload.info.countryRegionCity
            state.delivery.streetHouseApartmentZipcode = action.payload.info.streetHouseApartmentZipcode
            state.delivery.fullName = action.payload.info.fullName
            state.delivery.email = action.payload.info.email
            state.delivery.phoneNumber = action.payload.info.phoneNumber
            state.fullData = action.payload.fullData
        },
        [addDeliveryInfo.pending.toString()]: (state) => {
            state.loading = true;
        },
        [addDeliveryInfo.rejected.toString()]: (state, action) => {
            state.loading = false;
            state.error = action.payload
        },
        [addProduct.fulfilled.toString()]: (state, action) => {
            state.loading = false;
            state.products.push(action.payload)
        },
        [addProduct.pending.toString()]: (state) => {
            state.loading = true;
        },
        [addProduct.rejected.toString()]: (state, action) => {
            state.loading = false;
            state.error = action.payload
        },
        [initOrder.fulfilled.toString()]: (state, action) => {
            state.loading = false;

            state.fullData = action.payload.order
            state.orderId = action.payload.order.id
            //product info received from API
            state.products = action.payload.order.products
            state.price = action.payload.order.totalAmount
            state.currencyType = action.payload.order.currencyType

            if(state.products.length) {
                //product info received from frontend
                state.products[0].preview = action.payload?.productInfo?.preview
                state.products[0].name = action.payload?.productInfo?.name
                state.products[0].summary = action.payload?.productInfo?.summary
            }

            state.availableCurrencies = action.payload?.availableCurrencies
        },
        [initOrder.pending.toString()]: (state) => {
            state.loading = true;
        },
        [initOrder.rejected.toString()]: (state, action) => {
            state.loading = false;
            state.error = action.payload
        }
        ,
        [immediatePurchaseOrder.fulfilled.toString()]: (state, action) => {
            state.loading = false;

            if (action?.payload?.order?.ErrorStatusCode === 500) return;

            state.fullData = action.payload.order
            state.orderId = action.payload.order.id
            //product info received from API
            state.products = action.payload.order.products
            state.price = action.payload.order.totalAmount
            state.currencyType = action.payload.order.currencyType

            if(state.products?.length) {
                //product info received from frontend
                state.products[0].preview = action.payload.productInfo.preview
                state.products[0].name = action.payload.productInfo.name
                state.products[0].summary = action.payload.productInfo.summary
            }
        }
        ,
        [immediatePurchaseOrder.pending.toString()]: (state) => {
            state.loading = true;
        }
        ,
        [immediatePurchaseOrder.rejected.toString()]: (state, action) => {
            state.loading = false;
            state.error = action.payload
        },
        [checkAvailableCurrencies.fulfilled.toString()]: (state, action) => {
            state.availableCurrencies = action.payload.availableCurrencies
        }
        ,
        [checkAvailableCurrencies.pending.toString()]: (state) => {
            state.availableCurrencies = [];
        },
        [initOrderWithManyProducts.fulfilled.toString()]: (state, action) => {
            state.loading = false;

            state.fullData = action.payload.order
            state.orderId = action.payload.order.id
            //product info received from API
            state.products = action.payload.order.products
            state.price = action.payload.order.totalAmount
            state.currencyType = action.payload.order.currencyType

            //product info received from frontend
            action.payload.products.forEach((product: ProductRequest, index: number) => {
                state.products[index].preview = product.productInfo.preview
                state.products[index].name = product.productInfo.name
                state.products[index].summary = product.productInfo.summary
            })

            state.availableCurrencies = action.payload.availableCurrencies
        },
        [initOrderWithManyProducts.pending.toString()]: (state) => {
            state.loading = true;
        },
        [initOrderWithManyProducts.rejected.toString()]: (state, action) => {
            state.loading = false;
            state.error = action.payload
        },
        [getProductsPrices.pending.toString()]: (state) => {
            state.currenciesloading = true;
        },
        [getProductsPrices.fulfilled.toString()]: (state, action) => {
            let newCurrencies = action.payload.prices
            state.currenciesloading = false;
            state.availableCurrencies = newCurrencies
        },
        [getProductsPrices.rejected.toString()]: (state, action) => {
            state.currenciesloading = false;
            state.error = action.payload
        },
        [getAvailableTariffs.pending.toString()]: (state) => {
            state.tariffsLoading = true;
        },
        [getAvailableTariffs.fulfilled.toString()]: (state, action) => {
            state.tariffsLoading = false;
            state.availableTariffs = action.payload?.data
            if (action.payload?.data?.length && (action.payload?.entityType === EntityPaymentTypes.book || action.payload?.entityType === EntityPaymentTypes.card)) {
                const prevIndex = action.payload?.data?.findIndex((i: any) => i?.type === state.activeTariff?.type)
                state.activeTariff = action.payload?.data[(prevIndex !== -1 && prevIndex !== undefined) ? prevIndex : action.payload?.data?.length >= 2 ? 1 :0]
                return
            }
            if (action.payload?.data?.length) {
                state.activeTariff = action.payload?.data[0]
            }
        },
        [getAvailableTariffs.rejected.toString()]: (state, action) => {
            state.tariffsLoading = false;
            state.error = action.payload
        },
        [calculateCustomPrise.rejected.toString()]: (state, action) => {
            state.error = action.payload
        },
        [getDeliveryTypes.pending.toString()]: (state) => {
            state.deliveryTypes = null;
        },
        [getDeliveryTypes.fulfilled.toString()]: (state, action) => {
            if(action.payload?.data?.length) {
                state.deliveryTypes = action.payload?.data.map((item: any) => {
                    return { key: item.key, label: item.value }
                })
            }
        },
        [getDeliveryTypes.rejected.toString()]: (state, action) => {
            state.error = action.payload
        },
    },
});

export default OrderSlice;

export const selectorOrder = (state: any): OrderInitial => state.order
export const selectorDeliveryType = (language: string) => (state: any): OptionProps[] => API.toLang(state.order.deliveryTypes, language)

export const {
    clearFlowFields,
    clearOrderFields,
    clearOrderFieldsWithoutStep,
    clearImmediatePurchaseProps,
    clearProductField,
    changeCurrencyType,
    goToNextStep,
    goToPrevStep,
    goToStep,
    setActiveTariff,
    setPaymentCurrencies,
    setAvailableTariffs,
    setImmediatePurchaseProps,
    setFlowSize,
    setIsModerBuying,
    setFlowType,
    setFlowStep,
    setOrder,
    setProductFields,
    setProductFieldByKey,
    updateProductSummary,
    setBookCard,
    setBookCardT,
    clearBookCard,
    setTotalPrices
} = OrderSlice.actions
